<template>
	<!-- Визуальный индикатор режима разработчика -->
	<div v-if="isDevMode" class="dev-mode-indicator">🔧 DEVELOPER MODE</div>

	<!-- Основное содержимое с исправленными transitions -->
	<router-view v-slot="{ Component, route }">
		<Transition :name="getTransitionName(route)">
			<component :is="Component" :key="route.path" />
		</Transition>
	</router-view>

	<!-- Панель разработчика -->
	<DevToolsPanel />

	<!-- Уведомления (Toast) -->
	<NotificationToast />

	<!-- Модалка "Нет прав" -->
	<PermissionDeniedModal />
	<BlockedAccountModal />

	<!-- Глобальный индикатор сохранения -->
	<GlobalSaveIndicator />

	<!-- Индикатор статуса соединения -->
	<ConnectionStatus />
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import DevToolsPanel from '@/components/DevToolsPanel.vue'
import NotificationToast from '@/components/NotificationToast.vue'
import PermissionDeniedModal from '@/components/ui/PermissionDeniedModal.vue'
import BlockedAccountModal from '@/components/ui/BlockedAccountModal.vue'
import GlobalSaveIndicator from '@/components/ui/GlobalSaveIndicator.vue'
import ConnectionStatus from '@/components/ui/ConnectionStatus.vue'
import { isDevModeEnabled, initDevMode } from '@/utils/devMode'
import { useActivityTracker } from '@/composables/useActivityTracker'
import { useTokenExpirationWarning } from '@/composables/useTokenExpirationWarning'
import { useBitrix24Responsive } from '@/composables/useBitrix24Responsive'

export default {
	name: 'App',
	components: {
		DevToolsPanel,
		NotificationToast,
		PermissionDeniedModal,
		BlockedAccountModal,
		GlobalSaveIndicator,
		ConnectionStatus,
	},
	setup() {
		const isDevMode = ref(false)
		const route = useRoute()

		const applyLayoutClasses = currentRoute => {
			if (typeof document === 'undefined') return
			const path =
				typeof currentRoute === 'string'
					? currentRoute
					: currentRoute?.path || ''
			const disableBodyZoom =
				typeof currentRoute === 'object' &&
				!!currentRoute?.meta?.disableBodyZoom
			const isNotFoundRoute =
				typeof currentRoute === 'object' &&
				currentRoute?.name === 'Not404Found'
			const isAdmin = String(path || '').startsWith('/admin') && !isNotFoundRoute
			document.documentElement.classList.toggle('is-admin-layout', isAdmin)
			document.body.classList.toggle('is-admin-layout', isAdmin)
			document.documentElement.classList.toggle('force-no-zoom', disableBodyZoom)
			document.body.classList.toggle('force-no-zoom', disableBodyZoom)
		}

		// Инициализация отслеживания активности пользователя
		const { isTracking } = useActivityTracker({
			enabled: true, // Включено по умолчанию
		})

		// Инициализация мониторинга истечения токена
		useTokenExpirationWarning()

		// Инициализация адаптивности для Bitrix24
		useBitrix24Responsive()

		watch(
			() => route.fullPath,
			() => applyLayoutClasses(route),
			{ immediate: true }
		)

		onMounted(() => {
			// Инициализируем режим разработчика
			initDevMode()

			// Проверяем активен ли режим разработчика
			isDevMode.value = isDevModeEnabled()
			applyLayoutClasses(route)

			// Логируем статус отслеживания активности
			console.log(
				'[App] Activity tracking:',
				isTracking.value ? 'enabled' : 'disabled'
			)
		})

		/**
		 * Определяет имя transition анимации в зависимости от маршрута
		 * @param {Object} route - объект маршрута Vue Router
		 * @returns {String} - имя transition ('fade', 'slide', 'auth-transition')
		 */
		const getTransitionName = route => {
			if (route.name === 'Not404Found') return 'not-found-static'
			// Специальный переход для страницы авторизации
			if (route.path === '/auth' || route.name === 'AuthPage') {
				return 'auth-transition'
			}
			// Стандартный fade переход для всех остальных страниц
			return 'page-fade'
		}

		return {
			isDevMode,
			getTransitionName,
		}
	},
}
</script>

<style>
/* ========================================
   ВИЗУАЛЬНЫЙ ИНДИКАТОР РЕЖИМА РАЗРАБОТЧИКА
   ======================================== */
.dev-mode-indicator {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	background: linear-gradient(90deg, #ff6b6b 0%, #ffa500 50%, #ff6b6b 100%);
	background-size: 200% 100%;
	color: white;
	text-align: center;
	padding: 8px;
	font-weight: bold;
	font-size: 14px;
	z-index: 10000;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
	animation: gradient-shift 3s ease infinite;
	letter-spacing: 2px;
}

@keyframes gradient-shift {
	0% {
		background-position: 0% 50%;
	}
	50% {
		background-position: 100% 50%;
	}
	100% {
		background-position: 0% 50%;
	}
}

/* ========================================
   ГЛОБАЛЬНЫЕ ПЕРЕХОДЫ МЕЖДУ СТРАНИЦАМИ
   ======================================== */

/* === 1. СТАНДАРТНЫЙ FADE ПЕРЕХОД === */
/* Используется для большинства страниц */
.page-fade-enter-active {
	transition: opacity 0.3s ease-out;
	position: absolute;
	width: 100%;
}

.page-fade-leave-active {
	transition: opacity 0.2s ease-in;
	position: absolute;
	width: 100%;
}

.page-fade-enter-from {
	opacity: 0;
}

.page-fade-enter-to {
	opacity: 1;
}

.page-fade-leave-from {
	opacity: 1;
}

.page-fade-leave-to {
	opacity: 0;
}

/* === 2. СПЕЦИАЛЬНЫЙ ПЕРЕХОД ДЛЯ СТРАНИЦЫ AUTH === */
/* Упрощенный fade эффект для надежности */
.auth-transition-enter-active {
	transition: opacity 0.4s ease-out;
	position: absolute;
	width: 100%;
}

.auth-transition-leave-active {
	transition: opacity 0.3s ease-in;
	position: absolute;
	width: 100%;
}

.auth-transition-enter-from {
	opacity: 0;
}

.auth-transition-enter-to {
	opacity: 1;
}

.auth-transition-leave-from {
	opacity: 1;
}

.auth-transition-leave-to {
	opacity: 0;
}

/* === 3. ACCESSIBILITY: ОТКЛЮЧЕНИЕ АНИМАЦИЙ === */
/* Для пользователей с prefers-reduced-motion */
@media (prefers-reduced-motion: reduce) {
	.page-fade-enter-active,
	.page-fade-leave-active,
	.auth-transition-enter-active,
	.auth-transition-leave-active {
		transition: none !important;
		animation: none !important;
	}

	.page-fade-enter-from,
	.page-fade-leave-to,
	.auth-transition-enter-from,
	.auth-transition-leave-to {
		opacity: 1 !important;
		transform: none !important;
	}
}
</style>
